// modules/estrategias/habitante_calle/modal/ModalConsentimientoHabitanteCalle.js
import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import { subirConsentimientoHabitanteCalle, eliminarConsentimientoHabitanteCalle } from "../api/HabitanteCalleAPI";
import axiosClient from "../../../inicio/api/axiosClient";
import { useAuth } from "../../../inicio/context/AuthContext";
import { useOnlineStatus } from "../../../inicio/hooks/useSync";

export default function ModalConsentimientoHabitanteCalle({ show, onClose, persona, reload }) {
  const { user } = useAuth();
  const online = useOnlineStatus();
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  if (!persona) return null;

  const urlActual = persona.consentimiento_archivo_url
    ? `${axiosClient.defaults.baseURL}${persona.consentimiento_archivo_url}`
    : null;

  const puedeEliminar = user?.rol === "admin" || (persona.creado_por_id != null && persona.creado_por_id === user?.id);

  const cerrar = () => { setArchivo(null); onClose(); };

  const manejarSubmit = async (e) => {
    e.preventDefault();
    if (!archivo) {
      Swal.fire({ icon: "warning", title: "Selecciona un archivo PDF primero", confirmButtonColor: "#1a3a5c" });
      return;
    }
    try {
      setSubiendo(true);
      await subirConsentimientoHabitanteCalle(persona.id, archivo);
      await Swal.fire({ icon: "success", title: "Consentimiento guardado", timer: 1500, showConfirmButton: false });
      setArchivo(null);
      if (reload) await reload();
      onClose();
    } catch (error) {
      Swal.fire({
        icon: "error", title: "No se pudo subir el archivo",
        text: error.response?.data?.message || "Verifica que sea un PDF de menos de 10MB.",
        confirmButtonColor: "#dc3545",
      });
    } finally { setSubiendo(false); }
  };

  const manejarEliminar = async () => {
    const confirmacion = await Swal.fire({
      icon: "warning", title: "¿Eliminar el consentimiento?",
      text: "Vas a tener que volver a subirlo si lo necesitas después.",
      showCancelButton: true, confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmacion.isConfirmed) return;
    try {
      setEliminando(true);
      await eliminarConsentimientoHabitanteCalle(persona.id);
      await Swal.fire({ icon: "success", title: "Consentimiento eliminado", timer: 1500, showConfirmButton: false });
      if (reload) await reload();
      onClose();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo eliminar", text: error.response?.data?.message || "Intenta de nuevo.", confirmButtonColor: "#dc3545" });
    } finally { setEliminando(false); }
  };

  return (
    <Modal show={show} onHide={cerrar} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>✍️ Consentimiento informado</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
          {persona.primer_nombre} {persona.primer_apellido} — {persona.documento}
        </p>

        {urlActual && (
          <div className="alert alert-success py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
            <div className="d-flex justify-content-between align-items-center">
              <span>✅ Ya tiene un consentimiento cargado</span>
              <a href={urlActual} target="_blank" rel="noopener noreferrer" className="fw-bold">Ver documento</a>
            </div>
            {puedeEliminar && (
              <Button variant="outline-danger" size="sm" className="mt-2 w-100" onClick={manejarEliminar} disabled={!online || eliminando}>
                {eliminando ? "Eliminando..." : "🗑️ Eliminar consentimiento"}
              </Button>
            )}
          </div>
        )}

        {!online && (
          <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
            📴 Sin conexión — subir o eliminar el consentimiento requiere internet.
          </div>
        )}

        <Form onSubmit={manejarSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{urlActual ? "Reemplazar por un nuevo PDF" : "Archivo PDF"}</Form.Label>
            <Form.Control type="file" accept="application/pdf" disabled={!online || subiendo} onChange={e => setArchivo(e.target.files?.[0] || null)} />
            <Form.Text className="text-muted">Solo PDF, máximo 10MB.</Form.Text>
          </Form.Group>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" className="w-50" type="button" onClick={cerrar}>Cancelar</Button>
            <Button type="submit" className="w-50" disabled={!online || subiendo || !archivo} style={{ background: "#1a3a5c", border: "none" }}>
              {subiendo ? "Subiendo..." : "Guardar"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
